import React, { useMemo } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  ChevronLeft,
  ChevronRight,
  FlightTakeoff,
  SentimentVeryDissatisfied,
} from "@material-ui/icons";
import { formatLeaveDays, summarizeUserHolidaysForYear } from "../utils/holidayDates";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const BOOKED_COLOR = "#1565c0";
const REMAINING_COLOR = "#90caf9";
const SICKNESS_COLOR = "#c2185b";
const SICKNESS_TRACK = "#f8bbd0";

export default function HolidayDetailsView({
  detailsUser,
  holidays,
  sicknesses = [],
  year,
  onYearChange,
}) {
  const classes = useStyles();

  const holidaySummary = useMemo(() => {
    if (!detailsUser?.Id) {
      return {
        monthly: Array.from({ length: 12 }, () => 0),
        taken: 0,
        remaining: 28,
        entitlement: 28,
      };
    }
    return summarizeUserHolidaysForYear(holidays, detailsUser.Id, year, 28);
  }, [detailsUser, holidays, year]);

  const sicknessSummary = useMemo(() => {
    if (!detailsUser?.Id) {
      return {
        monthly: Array.from({ length: 12 }, () => 0),
        taken: 0,
        remaining: null,
        entitlement: null,
      };
    }
    return summarizeUserHolidaysForYear(
      sicknesses,
      detailsUser.Id,
      year,
      null
    );
  }, [detailsUser, sicknesses, year]);

  const holidayTakenPercent =
    holidaySummary.entitlement > 0
      ? Math.min(100, (holidaySummary.taken / holidaySummary.entitlement) * 100)
      : 0;

  const sicknessTakenPercent =
    sicknessSummary.taken > 0 ? 100 : 0;

  if (!detailsUser) {
    return (
      <Box className={classes.root}>
        <Typography variant="body1" color="textSecondary">
          No user selected
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.yearNav}>
        <IconButton
          size="small"
          onClick={() => onYearChange(year - 1)}
          aria-label="Previous year"
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" className={classes.yearTitle}>
          January {year} – December {year}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onYearChange(year + 1)}
          aria-label="Next year"
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <Box className={classes.summaryRow}>
        <Paper className={classes.summaryCard} elevation={1}>
          <Typography variant="subtitle1" className={classes.summaryTitle}>
            Holidays (Days)
          </Typography>
          <Box className={classes.summaryBody}>
            <Box className={classes.donutWrap}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={120}
                thickness={5}
                className={classes.donutTrack}
              />
              <CircularProgress
                variant="determinate"
                value={holidayTakenPercent}
                size={120}
                thickness={5}
                className={classes.donutProgress}
              />
              <Box className={classes.donutCenter}>
                <Typography variant="h4" className={classes.donutValue}>
                  {holidaySummary.entitlement}
                </Typography>
              </Box>
            </Box>

            <Box className={classes.legend}>
              <Box className={classes.legendItem}>
                <Box
                  className={classes.legendSwatch}
                  style={{ backgroundColor: BOOKED_COLOR }}
                />
                <Typography variant="body2" className={classes.legendText}>
                  {formatLeaveDays(holidaySummary.taken)} booked
                </Typography>
              </Box>
              <Box className={classes.legendItem}>
                <Box
                  className={classes.legendSwatch}
                  style={{ backgroundColor: REMAINING_COLOR }}
                />
                <Typography variant="body2" className={classes.legendText}>
                  {formatLeaveDays(holidaySummary.remaining)} remaining
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper className={classes.summaryCard} elevation={1}>
          <Typography variant="subtitle1" className={classes.summaryTitle}>
            Sickness (Days)
          </Typography>
          <Box className={classes.summaryBody}>
            <Box className={classes.donutWrap}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={120}
                thickness={5}
                className={classes.sicknessTrack}
              />
              <CircularProgress
                variant="determinate"
                value={sicknessTakenPercent}
                size={120}
                thickness={5}
                className={classes.sicknessProgress}
              />
              <Box className={classes.donutCenter}>
                <Typography variant="h4" className={classes.donutValue}>
                  {formatLeaveDays(sicknessSummary.taken)}
                </Typography>
              </Box>
            </Box>

            <Box className={classes.legend}>
              <Box className={classes.legendItem}>
                <Box
                  className={classes.legendSwatch}
                  style={{ backgroundColor: SICKNESS_COLOR }}
                />
                <Typography variant="body2" className={classes.legendText}>
                  {formatLeaveDays(sicknessSummary.taken)} taken
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Paper className={classes.monthCard} elevation={1}>
        {MONTH_NAMES.map((monthName, index) => (
          <Box key={monthName} className={classes.monthRow}>
            <Typography variant="body1" className={classes.monthName}>
              {monthName}
            </Typography>
            <Box className={classes.monthMetrics}>
              <Box className={classes.monthValue}>
                <Typography variant="body1" className={classes.monthDays}>
                  {formatLeaveDays(holidaySummary.monthly[index])}
                </Typography>
                <FlightTakeoff className={classes.flightIcon} />
              </Box>
              <Box className={classes.monthValue}>
                <Typography variant="body1" className={classes.monthDays}>
                  {formatLeaveDays(sicknessSummary.monthly[index])}
                </Typography>
                <SentimentVeryDissatisfied className={classes.sicknessIcon} />
              </Box>
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    maxWidth: 900,
  },
  yearNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  yearTitle: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
    minWidth: 260,
    textAlign: "center",
  },
  summaryRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    width: "fit-content",
    minWidth: 320,
  },
  summaryTitle: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
    marginBottom: theme.spacing(1.5),
  },
  summaryBody: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  donutWrap: {
    position: "relative",
    width: 120,
    height: 120,
  },
  donutTrack: {
    color: REMAINING_COLOR,
    position: "absolute",
    left: 0,
    top: 0,
  },
  donutProgress: {
    color: BOOKED_COLOR,
    position: "absolute",
    left: 0,
    top: 0,
  },
  sicknessTrack: {
    color: SICKNESS_TRACK,
    position: "absolute",
    left: 0,
    top: 0,
  },
  sicknessProgress: {
    color: SICKNESS_COLOR,
    position: "absolute",
    left: 0,
    top: 0,
  },
  donutCenter: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  donutValue: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
  },
  legend: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  legendSwatch: {
    width: 14,
    height: 14,
    borderRadius: 2,
    flexShrink: 0,
  },
  legendText: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
  },
  monthCard: {
    overflow: "hidden",
  },
  monthRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #e0e0e0",
    background:
      "linear-gradient(90deg, rgba(245,245,245,1) 0%, rgba(255,255,255,1) 100%)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  monthName: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
  },
  monthMetrics: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  monthValue: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 80,
    justifyContent: "flex-end",
  },
  monthDays: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
  },
  flightIcon: {
    color: BOOKED_COLOR,
    fontSize: 20,
  },
  sicknessIcon: {
    color: SICKNESS_COLOR,
    fontSize: 20,
  },
}));
